/**
 * Explore A3 组参：将 V1 壳参数映射为 module=125 对接字段。
 */
import { MODULE_ID_EXPLORE_A3 } from '@/constants/a3ModuleIds';
import {
  A3_DENOISE_I2I_DEFAULT,
  A3_DENOISE_I2I_ENHANCED,
  A3_DENOISE_T2I,
  A3_LORA_MAX,
  A3_PERTURB_DEFAULT,
  A3_PERTURB_MAX,
  A3_PERTURB_MIN,
  parseA3AspectSelection,
  resolveA3Size,
} from './constants';

function clampPerturb(value: unknown): number {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) return A3_PERTURB_DEFAULT;
  return Math.min(A3_PERTURB_MAX, Math.max(A3_PERTURB_MIN, n));
}

/** 解析比例与宽高；ImageSetting A3 选项 value 为「比例@宽x高」 */
function applyA3Resolution(params: any): void {
  if (typeof params.aspectRatios === 'string' && params.aspectRatios.includes('@')) {
    const parsed = parseA3AspectSelection(params.aspectRatios);
    params.aspectRatios = parsed.aspectRatios;
    params.width = parsed.width;
    params.height = parsed.height;
  } else {
    const size = resolveA3Size(params.aspectRatios, params.width, params.height);
    params.aspectRatios = size.aspectRatios;
    params.width = size.width;
    params.height = size.height;
  }
}

/** 参考图字段与重绘强度：有图走图生口径（参考权重即 denoise），无图走文生口径 */
function applyA3ReferenceParams(params: any): void {
  const layout = params.layoutRefer;
  const hasRefer = !!(layout?.image || params.layoutReferId);

  if (hasRefer) {
    params.originImgUrl = layout?.image ?? params.originImgUrl;
    params.originImgUrlId = layout?.id ?? params.layoutReferId ?? null;
    params.sketchType = params.sketchType === 1 ? 1 : 2;
    // 参考权重 → denoise；开启增强时产品默认 0.7，否则 0.8（若用户已调权重则用当前值）
    const weight = Number(params.layoutReferWeight);
    params.denoise = Number.isFinite(weight)
      ? weight
      : params.enablePerturb
        ? A3_DENOISE_I2I_ENHANCED
        : A3_DENOISE_I2I_DEFAULT;
  } else {
    delete params.originImgUrl;
    delete params.originImgUrlId;
    delete params.sketchType;
    // 文生权重口径为 1（需求 §5.1）；对接文档明确文生 denoise 后端不生效，仅对齐前端口径
    params.denoise = A3_DENOISE_T2I;
  }
}

/** 清理 A3 不支持的 V1 扩展字段（姿态/形象参考、局部重绘蒙版、炼金、负向描述词、面部精修、高清修复） */
function stripV1OnlyFields(params: any): void {
  params.layoutReferPose = null;
  params.styleReferList = [];
  params.styleReferIdList = [];
  delete params.maskImage;
  delete params.maskContrastImage;
  params.maskId = null;
  params.alchemy = false;
  // 负向描述词为 V1 独有块（A3 面板不展示，125 契约无此字段）
  delete params.negativePrompt;
  delete params.negativePromptWeight;
  // A3 不支持面部精修 / 高清修复；复用/再次生成可能残留 true，提交前强制关闭
  params.faceDetail = false;
  params.hdFix = false;
}

/**
 * 就地改造 Explore 生图参数为 A3 / module 125 契约。
 * @returns 改造后的 params（同一引用）
 */
export function applyExploreA3Params(params: any): any {
  params.module = MODULE_ID_EXPLORE_A3;
  applyA3Resolution(params);

  // LoRA 上限（A3_LORA_MAX）
  if (Array.isArray(params.loraList) && params.loraList.length > A3_LORA_MAX) {
    params.loraList = params.loraList.slice(0, A3_LORA_MAX);
  }
  if (!Array.isArray(params.loraList)) {
    params.loraList = [];
  }

  applyA3ReferenceParams(params);
  // 文生图、线稿参考、原图参考都是 1 或 2，未选或非法值默认 1
  params.batchSize = Number(params.batchSize) === 2 ? 2 : 1;

  params.enablePerturb = !!params.enablePerturb;
  if (params.enablePerturb) {
    params.perturb = clampPerturb(params.perturb ?? A3_PERTURB_DEFAULT);
  }

  stripV1OnlyFields(params);

  return params;
}

export function isExploreA3Ckpt(ckptType?: string | null): boolean {
  return ckptType === 'A3';
}
